// components/EditPhoneSystemModal.jsx
import React, { useState } from "react";
import { X, PhoneCall, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditPhoneSystemModal = ({
  phoneSystem,
  accountId,
  onClose,
  onSuccess,
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    systemType: phoneSystem.systemType || "",
    licenses: phoneSystem.licenses || "",
    notes: phoneSystem.notes || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const systemTypes = [
    "NEC on Prem",
    "Samsung on Prem",
    "IPECS",
    "Callswitch Legacy",
    "Callswitch One",
    "Webex",
    "Luminate",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.systemType) {
      setError("Please select a system type");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await apiService.updatePhoneSystem(accountId, phoneSystem.id, {
        ...formData,
        licenses: formData.licenses ? parseInt(formData.licenses) : null,
      });
      onSuccess();
    } catch (err) {
      console.error("Error updating phone system:", err);
      setError(err.response?.data?.message || "Failed to update phone system");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDarkMode
                  ? "bg-blue-600/10"
                  : "bg-blue-100 border border-blue-200"
              }`}
            >
              <PhoneCall
                size={20}
                className={isDarkMode ? "text-blue-400" : "text-blue-600"}
              />
            </div>
            <h2
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Edit Phone System
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <form
            onSubmit={handleSubmit}
            id="phoneSystemForm"
            className="space-y-6"
          >
            {error && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  isDarkMode
                    ? "bg-red-500/20 border border-red-500/30 text-red-400"
                    : "bg-red-50 border border-red-200 text-red-600"
                }`}
              >
                {error}
              </div>
            )}

            {/* System Type */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                System Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.systemType}
                onChange={(e) =>
                  setFormData({ ...formData, systemType: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
                required
              >
                <option value="">Select system type...</option>
                {systemTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Licenses */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Number of Licenses
              </label>
              <input
                type="number"
                min="0"
                value={formData.licenses}
                onChange={(e) =>
                  setFormData({ ...formData, licenses: e.target.value })
                }
                className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Enter number of licenses"
              />
            </div>

            {/* Notes */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={4}
                className={`w-full px-4 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Additional notes about this phone system..."
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="phoneSystemForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPhoneSystemModal;
